import {
  map,
  filter,
  switchMap,
  withLatestFrom,
  catchError,
  mergeMap,
  delay,
  tap
} from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect } from '@ngrx/effects';
import { ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import * as forms from '../actions/forms';
import * as notificationActions from '../actions/notifications';
import * as utils from '../shared/utils';
import * as fromRoot from '../reducers';

import { AccountsService } from '../services/accounts.service';

@Injectable()
export class AccountEffects {
  /**
   * Success Notification Effect
   */
  addSuccessNotification$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.AddSuccessNotification),
      map((action) => action.payload),
      withLatestFrom(this.store.select(fromRoot.notifications)),
      tap(() => this.router.navigateByUrl('/')),
      mergeMap((notifications) =>
        of(notificationActions.RemoveNotification(notifications[1][0].id)).pipe(
          delay(10000)
        )
      )
    )
  );

  /**
   * Failure Notification Effect
   */
  addFailureNotification$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(notificationActions.AddFailureNotification),
      map((action) => action.payload),
      withLatestFrom(this.store.select(fromRoot.notifications)),
      mergeMap((notifications) =>
        of(notificationActions.RemoveNotification(notifications[1][0].id)).pipe(
          delay(10000)
        )
      )
    )
  );

  /**
   * Reset Password Effects
   */
  resetPassword$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('resetPassword', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.resetPassword(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password Reset Successfully'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Verify Account Effects
   */
  verify$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('verify', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.verify(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Account Successfully Verified!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * New Verify Method Account Effects
   */
  verifyUser$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('verifyUserForm', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.verifyUser(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Account Successfully Verified!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Password Reset
   */
  passwordReset$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('passwordReset', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.passwordReset(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password reset requested!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Verify Password Reset
   */
  verifyPasswordReset$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('verifyPasswordReset', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.verifyPasswordReset(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password reset verified!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Cancel Password Reset
   */
  cancelPasswordReset$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('cancelPasswordReset', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.cancelPasswordReset(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password reset cancelled!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Submit email Change
   */
  submitEmailChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('changeEmail', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.submitEmailChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Email change submitted!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Verify email Change
   */
  verifyEmailChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('verifyEmailChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.verifyEmailChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification('Email change verified!')
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Cancel email Change
   */
  cancelEmailChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('cancelEmailChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.cancelEmailChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Email change cancelled!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Cancel email Change
   */
  revokeEmailChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('revokeEmailChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.revokeEmailChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification('Email change revoked!')
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Void email Change
   */
  voidEmailChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('voidEmailChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.voidEmailChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification('Email change voided!')
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Password Change
   */
  passwordChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('passwordChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.passwordChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password change requested!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Verify Password Change
   */
  verifyPasswordChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('verifyPasswordChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.verifyPasswordChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password change verified!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Cancel Password Change
   */
  cancelPasswordChange$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('cancelPasswordChange', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.cancelPasswordChange(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Password change canceled!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Create User Effects
   */
  createUser$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('createUser', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.createUser(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Account Successfully Created!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(
                notificationActions.AddFailureNotification(
                  err.error.errorMessage
                )
              )
            )
          )
        )
      )
    )
  );

  /**
   * Update Email Effects
   */
  updateEmail$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('updateEmail', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.updateEmail(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Email updated successfully!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Cancel Email Update Effects
   */
  cancelEmailUpdate$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('cancelEmailUpdate', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.cancelEmailRequest(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Email update cancelled successfully!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  /**
   * Unlock Account Effects
   */
  unlockAccount$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('unlockAccount', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.unlockAccount(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Account successfully unlocked!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  resendVCode$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(forms.FormRequestAction),
      map((action) => action.payload),
      filter((data) => utils.isForm('resendVCode', data)),
      withLatestFrom(this.store.select(fromRoot.getSport)),
      switchMap(([data, sport]) =>
        this.accountsService.resendVCode(sport, data).pipe(
          map((res) =>
            notificationActions.AddSuccessNotification(
              'Verification code resent!'
            )
          ),
          catchError((err) =>
            of(
              notificationActions.AddFailureNotification(err.error.errorMessage)
            )
          )
        )
      )
    )
  );

  constructor(
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    public accountsService: AccountsService,
    private router: Router
  ) {}
}
